import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { CommandParameter, ParameterOption } from '../../types/commands';

interface ParameterInputFactoryProps {
  parameter: CommandParameter;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

export const ParameterInputFactory: React.FC<ParameterInputFactoryProps> = ({ parameter, value, onChange, error }) => {
  const { display_name, is_required, validation_rules, ui_component, ui_options: options } = parameter;

  // Render based on UI component type
  switch (ui_component) {
    case 'text':
      return (
        <TextField
          fullWidth
          label={display_name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={is_required}
          error={!!error}
          helperText={error || validation_rules?.regex}
          inputProps={{
            minLength: validation_rules?.min_length,
            maxLength: validation_rules?.max_length,
          }}
        />
      );

    case 'number':
      return (
        <TextField
          fullWidth
          label={display_name}
          type="number"
          value={value || ''}
          onChange={(e) => onChange(Number(e.target.value))}
          required={is_required}
          error={!!error}
          helperText={error}
          inputProps={{
            min: validation_rules?.min,
            max: validation_rules?.max,
          }}
        />
      );

    case 'select':
      return (
        <FormControl fullWidth required={is_required} error={!!error}>
          <InputLabel>{display_name}</InputLabel>
          <Select value={value || ''} onChange={(e) => onChange(e.target.value)} label={display_name}>
            {options?.map((option: ParameterOption) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </FormControl>
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
          label={display_name}
        />
      );

    case 'radio':
      return (
        <Box>
          <Typography gutterBottom>{display_name}</Typography>
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="female"
              name="radio-buttons-group"
            >
              {options?.map((option: ParameterOption) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio checked={value === option.value} onChange={() => onChange(option.value)} />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
        </Box>
      );

    case 'multiselect':
      return (
        <Autocomplete
          multiple
          options={options || []}
          getOptionLabel={(option: ParameterOption) => option.label}
          value={Array.isArray(value) ? options?.filter((opt) => value.includes(opt.value)) : []}
          onChange={(_e, newValue) => onChange(newValue.map((opt) => opt.value))}
          renderInput={(params) => (
            <TextField {...params} label={display_name} required={is_required} error={!!error} helperText={error} />
          )}
        />
      );

    case 'textarea':
      return (
        <TextField
          fullWidth
          label={display_name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={is_required}
          error={!!error}
          helperText={error}
          multiline
          rows={4}
          inputProps={{
            minLength: validation_rules?.min_length,
            maxLength: validation_rules?.max_length,
          }}
        />
      );

    default:
      return (
        <TextField
          fullWidth
          label={display_name}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={is_required}
          error={!!error}
          helperText={error}
        />
      );
  }
};
